import './application-contacts-block.scss'

import {
  useCallback,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ContentCopy as ContentCopyIcon,
} from '@vedmarket/ui-kit'

import ApplicationPhoneField from './application-phone-field'

const COPY_ICON_SIZE = 16

const ApplicationContactsBlock = ({
  title,
  fields,
  editable = false,
  onFieldChange,
  fieldErrors,
  onSave,
  saveLabel,
  saveDisabled = false,
  saving = false,
}: ApplicationContactsBlockProps) => {
  const { t } = useTranslation()
  const [ copiedFieldKey, setCopiedFieldKey ] = useState<string | null>(null)

  const visibleFields = editable
    ? fields
    : fields.filter((field) => field.value.trim())

  const handleCopy = useCallback(async (field: ApplicationContactFieldView) => {
    const text = field.value.trim()
    if (!text) {
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      setCopiedFieldKey(field.key)
      window.setTimeout(() => setCopiedFieldKey(null), 2000)
    } catch {
      setCopiedFieldKey(null)
    }
  }, [])

  if (!visibleFields.length) {
    return null
  }

  return (
    <Box className='application-contacts-block'>
      <Typography variant='subtitle2' component='h4' className='application-contacts-block__title'>
        {title}
      </Typography>
      <Box className='application-contacts-block__rows' component='ul'>
        {visibleFields.map((field) => {
          const copied = copiedFieldKey === field.key
          const copyLabel = copied
            ? t('applications__text__contactCopied')
            : t('applications__text__copyToClipboard')
          const canCopy = Boolean(field.value.trim())
          const error = fieldErrors?.[field.key]

          const copyButton = canCopy ? (
            <Tooltip title={copyLabel}>
              <IconButton
                type='button'
                size='small'
                edge='end'
                className='application-contacts-block__copy'
                aria-label={copyLabel}
                onClick={() => handleCopy(field)}
              >
                <ContentCopyIcon style={{ width: COPY_ICON_SIZE, height: COPY_ICON_SIZE }} />
              </IconButton>
            </Tooltip>
          ) : undefined

          return (
            <Box
              key={field.key}
              component='li'
              className='application-contacts-block__field'
            >
              {editable && field.key.endsWith('_phone') ? (
                <ApplicationPhoneField
                  label={field.label}
                  value={field.value}
                  error={error}
                  onChange={(value) => onFieldChange?.(field.key, value)}
                  endAdornment={copyButton}
                />
              ) : <TextField
                type={field.key.endsWith('_email') ? 'email' : 'text'}
                autoComplete={field.key.endsWith('_email') ? 'email' : undefined}
                fullWidth
                size='small'
                InputLabelProps={{ shrink: true }}
                className='application-contacts-block__input'
                value={field.value}
                error={Boolean(error)}
                helperText={error ? (
                  <Typography component='span' variant='inherit'>{error}</Typography>
                ) : undefined}
                label={(
                  <Typography component='span' variant='inherit' color={error ? 'error.main' : 'text.primary'}>
                    {field.label}
                  </Typography>
                )}
                onChange={editable && onFieldChange ? (e) => onFieldChange(field.key, e.target.value) : undefined}
                InputProps={{
                  readOnly: !editable,
                  endAdornment: canCopy ? (
                    <InputAdornment position='end'>
                      {copyButton}
                    </InputAdornment>
                  ) : undefined,
                }}
              />}
            </Box>
          )
        })}
      </Box>
      {onSave && (
        <Button
          variant='outlined'
          className='application-contacts-block__save'
          disabled={saveDisabled || saving}
          onClick={onSave}
        >
          <Typography component='span' variant='inherit'>
            {saveLabel}
          </Typography>
        </Button>
      )}
    </Box>
  )
}

export default ApplicationContactsBlock
